import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Gauge, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EXPENSE_CATS } from "@/components/finance/ExpensesCard";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";
import { cn } from "@/lib/utils";

const BAR = { ok: "bg-sage", warning: "bg-butter", over: "bg-destructive" };

const BudgetForm = ({ base, used, onDone }) => {
  const free = Object.keys(EXPENSE_CATS).filter((k) => !used.includes(k));
  const [v, setV] = useState({ category: free[0] || "autre", limit: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`${base}/budgets/${v.category}`, { limit: Number(v.limit) });
      setV({ category: free.find((k) => k !== v.category) || v.category, limit: "" });
      toast.success("Budget enregistré");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  if (!free.length) return null;
  return (
    <form onSubmit={submit} className="grid gap-2 sm:grid-cols-[12rem_1fr_auto]" data-testid="budget-form">
      <Select value={v.category} onValueChange={(c) => setV({ ...v, category: c })}>
        <SelectTrigger data-testid="budget-category-select"><SelectValue /></SelectTrigger>
        <SelectContent>{free.map((k) => <SelectItem key={k} value={k}>{EXPENSE_CATS[k]}</SelectItem>)}</SelectContent>
      </Select>
      <Input type="number" min="1" step="1" value={v.limit} onChange={(e) => setV({ ...v, limit: e.target.value })} placeholder="Plafond € / mois" required data-testid="budget-limit-input" />
      <Button type="submit" className="rounded-full sm:px-8" data-testid="budget-submit-button">Définir</Button>
    </form>
  );
};

const LimitEditor = ({ b, base, onDone, onCancel }) => {
  const [limit, setLimit] = useState(String(b.limit));
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`${base}/budgets/${b.category}`, { limit: Number(limit) });
      toast.success(`Budget ${b.label} modifié`);
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={save} className="mt-2 flex items-center gap-2" data-testid={`budget-edit-form-${b.category}`}>
      <Input type="number" min="1" step="1" value={limit} onChange={(e) => setLimit(e.target.value)} required autoFocus className="h-9" data-testid={`budget-edit-input-${b.category}`} />
      <Button type="submit" size="icon" className="h-9 w-9 shrink-0 rounded-full" aria-label="Enregistrer" data-testid={`budget-edit-save-${b.category}`}><Check className="h-4 w-4" /></Button>
      <Button type="button" size="icon" variant="ghost" className="h-9 w-9 shrink-0 rounded-full" onClick={onCancel} aria-label="Annuler" data-testid={`budget-edit-cancel-${b.category}`}><X className="h-4 w-4" /></Button>
    </form>
  );
};

const BudgetRow = ({ b, base, canEdit, onChange }) => {
  const { money } = useFormat();
  const [editing, setEditing] = useState(false);
  const pct = b.pct ?? 0;
  const remove = async () => { try { await api.delete(`${base}/budgets/${b.category}`); onChange(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <li className="rounded-xl border border-border bg-card/70 p-4 text-sm" data-testid={`budget-${b.category}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold">{b.label}</span>
        <span className="flex items-center gap-1">
          <span className={cn("num mr-1 text-xs font-bold", b.status === "over" ? "text-destructive" : b.status === "warning" ? "text-foreground" : "text-muted-foreground")} data-testid={`budget-pct-${b.category}`}>{pct} %</span>
          {canEdit && !editing && <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={() => setEditing(true)} aria-label="Modifier le plafond" data-testid={`budget-edit-${b.category}`}><Pencil className="h-3.5 w-3.5" /></Button>}
          {canEdit && <ConfirmDelete title="Supprimer ce budget ?" text={`Le plafond « ${b.label} » sera supprimé.`} testid={`budget-delete-${b.category}`} onConfirm={remove} />}
        </span>
      </div>
      {editing ? <LimitEditor b={b} base={base} onCancel={() => setEditing(false)} onDone={() => { setEditing(false); onChange(); }} /> : (
        <>
          <div className="mt-2 h-2 rounded-full bg-secondary"><div className={cn("h-full rounded-full transition-[width] duration-500", BAR[b.status])} style={{ width: `${Math.min(100, pct)}%` }} /></div>
          <div className="mt-1.5 text-xs text-muted-foreground"><span className="num">{money(b.spent, { maximumFractionDigits: 2 })}</span> sur <span className="num">{money(b.limit)}</span>{b.status === "over" && <span className="font-semibold text-destructive"> · dépassé</span>}</div>
        </>
      )}
    </li>
  );
};

export const BudgetsCard = ({ base = "/finance", canEdit = true, title = "Budgets mensuels" }) => {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["budgets", base], queryFn: () => api.get(`${base}/budgets`).then((r) => r.data) });
  const refresh = () => ["budgets", "overview", "dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  return (
    <section className="card-surface tone-wash tone-mint space-y-5 p-6" data-testid="budgets-card">
      <div className="flex items-center gap-3">
        <span className="tone-chip flex h-9 w-9 items-center justify-center rounded-xl"><Gauge className="h-4 w-4" /></span>
        <div className="flex-1"><h2 className="text-lg font-bold">{title}</h2>
          <p className="text-xs text-muted-foreground">{canEdit ? "Un plafond par catégorie, modifiable à tout moment. Alerte à 80 % puis en cas de dépassement." : "Plafonds fixés par les responsables du foyer (consultation seule)."}</p></div>
      </div>
      {canEdit && <BudgetForm key={data.length} base={base} used={data.map((b) => b.category)} onDone={refresh} />}
      {data.length ? (
        <ul className="grid gap-3 sm:grid-cols-2" data-testid="budgets-list">
          {data.map((b) => <BudgetRow key={b.category} b={b} base={base} canEdit={canEdit} onChange={refresh} />)}
        </ul>
      ) : <p className="text-sm text-muted-foreground">Aucun budget défini.</p>}
    </section>
  );
};
